import { ImpactFundPricingModel, ImpactType, PublicationStatus } from '@prisma/client';
import { z } from 'zod';
import Decimal from 'decimal.js';
import {
  impactShopCurrencyPricesSchema,
  impactShopIdSchema,
  impactShopSlugSchema,
  type ImpactShopReadiness,
  type ImpactShopReadinessIssue,
} from '../impact-products/impact-product-schemas';

export const impactFundPricingModelSchema = z.nativeEnum(ImpactFundPricingModel);
export const impactFundImpactTypeSchema = z.nativeEnum(ImpactType).nullable();

export const impactFundFields = {
  slug: impactShopSlugSchema,
  title: z.string().trim().min(1).max(191),
  shortDescription: z.string().trim().min(1).max(191),
  description: z.string().trim().min(1).nullable(),
  impactType: impactFundImpactTypeSchema,
  pricingModel: impactFundPricingModelSchema,
};

export const impactFundSchema = z
  .object(impactFundFields)
  .strict()
  .superRefine((fund, ctx) => {
    if (fund.pricingModel === ImpactFundPricingModel.FIXED_UNIT_PRICE && fund.impactType === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['impactType'],
        message: 'Unit-priced funds must have an impact type',
      });
    }
    if (fund.pricingModel === ImpactFundPricingModel.VARIABLE_CONTRIBUTION && fund.impactType !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['impactType'],
        message: 'Donation funds cannot have an impact type',
      });
    }
  });

export const impactFundImageFields = {
  imageKey: z.string().trim().min(1).max(191),
  alt: z.string().trim().max(191).nullable(),
};

const impactFundAllocationFields = {
  projectId: impactShopIdSchema,
  percentage: z
    .number()
    .positive()
    .max(1)
    .finite()
    .refine(value => new Decimal(value).decimalPlaces() <= 9, 'Must have no more than 9 decimal places'),
};

const manualImpactFundAllocationSchema = z
  .object({
    ...impactFundAllocationFields,
    inheritProjectPrice: z.literal(false).default(false),
    unitPrices: impactShopCurrencyPricesSchema.optional(),
  })
  .transform((allocation, ctx) => {
    if (!allocation.unitPrices) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['unitPrices'],
        message: 'Manual allocation prices are required when project price inheritance is disabled',
      });
      return z.NEVER;
    }
    return { ...allocation, unitPrices: allocation.unitPrices };
  });

export const impactFundAllocationSchema = z
  .union([
    z.object({
      ...impactFundAllocationFields,
      inheritProjectPrice: z.literal(true),
      unitPrices: impactShopCurrencyPricesSchema.nullable().optional(),
    }),
    manualImpactFundAllocationSchema,
  ])
  .transform(allocation => (allocation.inheritProjectPrice ? { ...allocation, unitPrices: null } : allocation));

export const impactFundAllocationsSchema = z.array(impactFundAllocationSchema).superRefine((allocations, ctx) => {
  const projectIds = allocations.map(allocation => allocation.projectId);
  if (new Set(projectIds).size !== projectIds.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Each project can only be allocated once',
    });
  }
});

export type ImpactFundActivationValidationInput = {
  title: string;
  shortDescription: string;
  description: string | null;
  pricingModel: ImpactFundPricingModel;
  impactType: ImpactType | null;
  imageCount: number;
  allocations: Array<{
    percentage: string | number;
    unitPrices: {
      GBP: string | number;
      USD: string | number;
      EUR: string | number;
      CAD: string | number;
      AUD: string | number;
    } | null;
    inheritProjectPrice?: boolean;
    project: { status: PublicationStatus; impactType: ImpactType };
  }>;
};

export function getImpactFundActivationReadiness(input: ImpactFundActivationValidationInput): ImpactShopReadiness {
  const issues: ImpactShopReadinessIssue[] = [];

  if (!input.title.trim() || !input.description?.trim()) {
    issues.push({
      code: 'FUND_CONTENT_REQUIRED',
      path: ['fund'],
      message: 'An active fund requires a title and description',
    });
  }

  if (!input.shortDescription.trim()) {
    issues.push({
      code: 'FUND_SHORT_DESCRIPTION_REQUIRED',
      path: ['fund', 'shortDescription'],
      message: 'An active fund requires a short description',
    });
  }

  if (input.pricingModel === ImpactFundPricingModel.FIXED_UNIT_PRICE && input.impactType === null) {
    issues.push({
      code: 'FUND_IMPACT_TYPE_REQUIRED',
      path: ['fund', 'impactType'],
      message: 'A unit-priced fund requires an impact type',
    });
  }

  if (input.imageCount === 0) {
    issues.push({
      code: 'FUND_IMAGE_REQUIRED',
      path: ['fund', 'images'],
      message: 'An active fund requires at least one image',
    });
  }

  if (input.allocations.length === 0) {
    issues.push({
      code: 'FUND_ALLOCATIONS_REQUIRED',
      path: ['fund', 'allocations'],
      message: 'An active fund requires at least one allocation',
    });
  } else {
    if (
      input.allocations.some(allocation => {
        const percentage = new Decimal(allocation.percentage);
        return percentage.lessThanOrEqualTo(0) || percentage.greaterThan(1) || percentage.decimalPlaces() > 9;
      })
    ) {
      issues.push({
        code: 'FUND_ALLOCATION_PERCENTAGE_INVALID',
        path: ['fund', 'allocations'],
        message: 'Every allocation percentage must be greater than 0 and no more than 1',
      });
    }

    const allocationTotal = input.allocations.reduce(
      (total, allocation) => total.plus(allocation.percentage),
      new Decimal(0),
    );
    if (!allocationTotal.equals(1)) {
      issues.push({
        code: 'FUND_ALLOCATION_TOTAL_INVALID',
        path: ['fund', 'allocations'],
        message: 'Fund allocations must total 1',
      });
    }

    if (
      input.allocations.some(
        allocation =>
          (!allocation.inheritProjectPrice && allocation.unitPrices === null) ||
          (allocation.unitPrices !== null &&
            Object.values(allocation.unitPrices).some(price => {
              const decimalPrice = new Decimal(price);
              return decimalPrice.lessThanOrEqualTo(0) || decimalPrice.decimalPlaces() > 6;
            })),
      )
    ) {
      issues.push({
        code: 'FUND_ALLOCATION_PRICES_INVALID',
        path: ['fund', 'allocations'],
        message: 'Every allocation requires a positive unit price in each currency',
      });
    }

    if (input.allocations.some(allocation => allocation.inheritProjectPrice && allocation.unitPrices === null)) {
      issues.push({
        code: 'FUND_ALLOCATION_PROJECT_PRICES_REQUIRED',
        path: ['fund', 'allocations'],
        message: 'Add project prices before inheriting them in a fund allocation',
      });
    }

    if (input.allocations.some(allocation => allocation.project.status !== PublicationStatus.LIVE)) {
      issues.push({
        code: 'FUND_ALLOCATION_PROJECT_NOT_LIVE',
        path: ['fund', 'allocations'],
        message: 'Every allocated project must be live',
      });
    }

    if (
      input.impactType !== null &&
      input.allocations.some(allocation => allocation.project.impactType !== input.impactType)
    ) {
      issues.push({
        code: 'FUND_ALLOCATION_IMPACT_TYPE_INVALID',
        path: ['fund', 'allocations'],
        message: 'Every allocated project must match the fund impact type',
      });
    }
  }

  return { isReady: issues.length === 0, issues };
}
